import { z } from 'zod';

/**
 * Imágenes de la consola: logos de empresa, fotos de perfil y favicons de las tiendas (leads).
 * Todo se sirve desde el mismo origen (la CSP sólo permite `img-src 'self' data:`).
 */

/** Tipos de imagen aceptados (se validan por los bytes, no por lo que diga el cliente). SVG no: puede llevar scripts. */
export const IMAGE_MIMES = [
  'image/png',
  'image/jpeg',
  'image/webp',
  'image/gif',
  'image/x-icon',
  'image/avif',
] as const;
export type ImageMime = (typeof IMAGE_MIMES)[number];

/** Tope de una foto de perfil o un logo subido (en bytes, ya decodificado). */
export const UPLOAD_IMAGE_MAX_BYTES = 512 * 1024;

export const uploadImageSchema = z.object({
  /** Contenido en base64 (con o sin prefijo `data:…;base64,`). */
  contentBase64: z.string().min(4),
});
export type UploadImage = z.infer<typeof uploadImageSchema>;

/**
 * Dominio de una web tal como lo escribe una persona ("https://www.tienda.es/", "tienda.es") →
 * hostname en minúsculas, sin esquema, puerto, ruta ni punto final. `null` si no es un dominio
 * público plausible (IPs, `localhost`, TLDs internos).
 */
export function normalizeWebsite(input: string): string | null {
  let s = input.trim().toLowerCase();
  if (!s) return null;
  s = s.replace(/^[a-z][a-z0-9+.-]*:\/\//, '');
  s = s.replace(/[/?#].*$/, '');
  s = s.replace(/^[^@]*@/, '');
  s = s.replace(/:\d+$/, '');
  s = s.replace(/\.$/, '');
  if (s.length > 253 || !s.includes('.')) return null;
  // Sólo nombres DNS (IDN ya en punycode, "xn--"). Un literal IP no pasa: el TLD tiene que tener letras.
  const labels = s.split('.');
  if (!labels.every((l) => /^[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?$/.test(l))) return null;
  const tld = labels[labels.length - 1] ?? '';
  if (!/^(?:[a-z]{2,63}|xn--[a-z0-9-]{1,59})$/.test(tld)) return null;
  if (
    [
      'localhost',
      'local',
      'internal',
      'lan',
      'home',
      'corp',
      'arpa',
      'test',
      'invalid',
      'example',
    ].includes(tld)
  ) {
    return null;
  }
  return s;
}

export const updateWebsiteSchema = z.object({
  /** `null` o vacío = sin web (vuelve el monograma). */
  website: z.string().max(300).nullable(),
});
export type UpdateWebsite = z.infer<typeof updateWebsiteSchema>;

export const tenantBrandingSchema = z.object({
  tenantId: z.uuid(),
  website: z.string().nullable(),
  /** URL (mismo origen) del logo a mostrar: el subido, si hay; si no, el favicon de la web. */
  logoUrl: z.string().nullable(),
  hasUploadedLogo: z.boolean(),
});
export type TenantBranding = z.infer<typeof tenantBrandingSchema>;

export const avatarResponseSchema = z.object({ avatarUrl: z.string().nullable() });
export type AvatarResponse = z.infer<typeof avatarResponseSchema>;

/** URL del favicon de un dominio (lo resuelve y cachea la API). */
export function logoUrlForDomain(domain: string | null | undefined): string | null {
  const d = domain ? normalizeWebsite(domain) : null;
  return d ? `/api/logos/${encodeURIComponent(d)}` : null;
}

/** Freemail: el dominio del email no es el de la empresa. */
const FREEMAIL = new Set([
  'gmail.com',
  'googlemail.com',
  'hotmail.com',
  'hotmail.es',
  'outlook.com',
  'outlook.es',
  'live.com',
  'yahoo.com',
  'yahoo.es',
  'icloud.com',
  'me.com',
  'msn.com',
  'aol.com',
  'proton.me',
  'protonmail.com',
  'gmx.com',
  'gmx.es',
  'telefonica.net',
  'movistar.es',
]);

/** Dominio de empresa a partir de un email (`null` si es freemail o no parece un email). */
export function domainFromEmail(email: string | null | undefined): string | null {
  const at = email?.lastIndexOf('@') ?? -1;
  if (!email || at < 1) return null;
  const d = normalizeWebsite(email.slice(at + 1));
  return d && !FREEMAIL.has(d) ? d : null;
}
